import { useState, useEffect, useMemo, type ComponentType } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  Eye,
  Clock,
  Smartphone,
  Globe,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Settings,
  DollarSign,
  MapPin,
  Chrome,
  BarChart3,
  LineChart as LineChartIcon,
  Zap,
  Share2,
  ArrowUp,
  ArrowDown,
  Minus,
  LogOut,
} from "lucide-react";
import { agruparPorPais } from "@/lib/ga4-geography";
import { agruparPorNavegador, proporcaoDoLider } from "@/lib/ga4-technology";
import { useIntegrationStatus } from "@/hooks/useIntegrationStatus";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import {
  LineChart as TremorLineChart,
  BarChart as TremorBarChart,
  DonutChart as TremorDonutChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { useToast } from "@/hooks/use-toast";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import {
  formatCurrency as formatCurrencyUtil,
  formatPercent as formatPercentUtil,
  formatInteger as formatIntegerUtil,
} from "@/utils/numberFormat";
import {
  disconnectGoogleAnalyticsApiV1IntegrationsGoogleAnalyticsDisconnectDelete,
  getCompleteDataApiV1IntegrationsGoogleAnalyticsCompleteDataGet,
  getGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsAccountsGet,
  getGoogleAnalyticsAuthUrlApiV1IntegrationsNexusGoogleAnalyticsAuthUrlGet,
  getGoogleAnalyticsOverviewApiV1IntegrationsGoogleAnalyticsOverviewGet,
  getGoogleAnalyticsStatusApiV1IntegrationsGoogleAnalyticsStatusGet,
  syncGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsSyncAccountsPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  GoogleAnalyticsAccount,
  GoogleAnalyticsCompleteResponse,
  GoogleAnalyticsConnectionStatus,
  GoogleAnalyticsOverviewData as GeneratedGoogleAnalyticsOverviewData,
} from "@/backend-sdk/client/types.gen";

type GoogleAnalyticsOverviewData = GeneratedGoogleAnalyticsOverviewData;
type GoogleAnalyticsStatus = GoogleAnalyticsConnectionStatus;
type GoogleAnalyticsCompleteData = GoogleAnalyticsCompleteResponse;
type GoogleAnalyticsDailyMetric =
  GoogleAnalyticsCompleteData["daily_metrics"][number];
type GoogleAnalyticsGeographyMetric =
  GoogleAnalyticsCompleteData["geography_data"][number];
type GoogleAnalyticsTechnologyMetric =
  GoogleAnalyticsCompleteData["technology_data"][number];
type GoogleAnalyticsPageMetric =
  GoogleAnalyticsCompleteData["page_metrics"][number];
type GoogleAnalyticsRealtimeData = NonNullable<
  GoogleAnalyticsCompleteData["realtime_data"]
>;

type TopPageSummary = {
  page_title: string;
  page_path: string;
  pageviews: number;
  avg_time_on_page: number;
  bounce_rate: number;
  exit_rate: number;
};

type TopPageAccumulator = TopPageSummary & {
  count: number;
};

interface ChartDataPoint {
  name: string;
  usuarios: number;
  sessoes: number;
  pageviews: number;
  revenue: number;
  conversoes: number;
}

interface DeviceDataPoint {
  name: string;
  value: number;
  color: string;
}

interface ChannelDataPoint {
  name: string;
  sessions: number;
  users: number;
  revenue: number;
  percentage: number;
  color: string;
}

interface EcommerceFunnelPoint {
  stage: string;
  users: number;
  color: string;
}

/** Todos os números desta tela saem da propriedade do GA4. */
const FONTES_GA4: readonly RequisitoDeFonte[] = ["ga4"];

/** `null` quando o campo não veio — vira N/D, e não zero. */
const numeroOuNulo = (valor: number | null | undefined): number | null =>
  valor === null || valor === undefined ? null : Number(valor);

const ChartSkeleton = ({ height = 350 }: { height?: number }) => (
  <div className="p-4">
    <Skeleton className={`w-full h-[${height}px]`} />
  </div>
);

const GoogleAnalyticsPage = () => {
  const { filter } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filter);
  const { toast } = useToast();

  // Hook de health monitoring das integrações
  const {
    health,
    status: integrationStatus,
    isLoading: healthLoading,
    forceCheck,
  } = useIntegrationStatus("google_analytics", "google-analytics-main");

  // Estados principais
  const [status, setStatus] = useState<GoogleAnalyticsStatus | null>(null);
  const [accounts, setAccounts] = useState<GoogleAnalyticsAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  // Começa ligado: a busca inicial espera um segundo, e nesse intervalo a tela caía no
  // "Google Analytics não conectado" e nos cards zerados, antes de saber de qualquer coisa.
  const [loading, setLoading] = useState(true);
  // Falhas separadas: o resumo (cards, e-commerce, comportamento) e os dados completos
  // (tempo real). Antes as duas só viravam toast, e os números ficavam em zero.
  const [erroResumo, setErroResumo] = useState(false);
  const [erroDados, setErroDados] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Estados de dados - usando dados completos da nova API
  const [completeData, setCompleteData] =
    useState<GoogleAnalyticsCompleteData | null>(null);
  const [overviewData, setOverviewData] =
    useState<GoogleAnalyticsOverviewData | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [deviceData, setDeviceData] = useState<DeviceDataPoint[]>([]);
  const [channelData, setChannelData] = useState<ChannelDataPoint[]>([]);
  const [geographyData, setGeographyData] = useState<
    GoogleAnalyticsGeographyMetric[]
  >([]);
  const [technologyData, setTechnologyData] = useState<
    GoogleAnalyticsTechnologyMetric[]
  >([]);
  const [topPages, setTopPages] = useState<TopPageSummary[]>([]);

  /**
   * O card NAVEGADORES, agregado por navegador — ver `lib/ga4-technology.ts`, que explica
   * por que `technology_data.slice(0, 10)` nunca foi uma distribuição.
   */
  const topBrowsers = useMemo(
    () => agruparPorNavegador(technologyData),
    [technologyData],
  );

  /**
   * O card PRINCIPAIS PAÍSES, agregado por país — ver `lib/ga4-geography.ts`, que explica a
   * taxa ponderada por sessões (M-194).
   */
  const topCountries = useMemo(
    () => agruparPorPais(geographyData),
    [geographyData],
  );
  const [realtimeData, setRealtimeData] =
    useState<GoogleAnalyticsRealtimeData | null>(null);
  const [ecommerceFunnel, setEcommerceFunnel] = useState<
    EcommerceFunnelPoint[]
  >([]);

  // Função para formatar datas
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  // Calcular período padrão (últimos 30 dias)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadingData(true);

      // Verificar status da conexão
      const { data: connectionStatus } =
        await getGoogleAnalyticsStatusApiV1IntegrationsGoogleAnalyticsStatusGet(
          { throwOnError: true },
        );
      setStatus(connectionStatus);

      // Carregar contas
      const { data: accountsData } =
        await getGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsAccountsGet(
          { throwOnError: true },
        );
      setAccounts(accountsData);

      if (accountsData.length > 0) {
        const firstAccount = accountsData[0];

        if (!selectedAccount) {
          setSelectedAccount(firstAccount.id);

          toast({
            title: "Carregando Dados",
            description: "Buscando dados do Google Analytics...",
          });

          // eslint-disable-next-line react-hooks/immutability
          await loadAccountData(firstAccount.id);

          toast({
            title: "Dados Carregados",
            description: "Dados do Google Analytics carregados com sucesso!",
          });
        } else {
          await loadAccountData(selectedAccount);
        }
      } else {
        // Só tentar sincronizar se a integração estiver conectada
        if (connectionStatus.is_connected) {
          try {
            toast({
              title: "Sincronizando Contas",
              description: "Buscando propriedades do Google Analytics...",
            });

            await syncGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsSyncAccountsPost(
              { throwOnError: true },
            );

            // Recarregar contas após sincronização
            const { data: updatedAccounts } =
              await getGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsAccountsGet(
                { throwOnError: true },
              );
            setAccounts(updatedAccounts);

            if (updatedAccounts.length > 0) {
              const firstAccount = updatedAccounts[0];
              setSelectedAccount(firstAccount.id);
              await loadAccountData(firstAccount.id);

              toast({
                title: "Sucesso",
                description: "Propriedades sincronizadas e dados carregados!",
              });
            } else {
              // eslint-disable-next-line react-hooks/immutability
              clearAllData();
              toast({
                title: "Nenhuma Propriedade",
                description: "Nenhuma propriedade Google Analytics encontrada",
                variant: "destructive",
              });
            }
          } catch (syncError) {
            clearAllData();
            toast({
              title: "Erro",
              description:
                "Erro ao sincronizar propriedades do Google Analytics",
              variant: "destructive",
            });
          }
        } else {
          // Se não está conectado, apenas limpar dados
          clearAllData();
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do Google Analytics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setLoadingData(false);
    }
  };

  // Carregar dados na inicialização
  useEffect(() => {
    // Só na montagem. A guarda de "já está carregando" saiu: `loading` agora nasce ligado.
    const timer = setTimeout(() => {
      loadData();
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllData = () => {
    setCompleteData(null);
    setOverviewData(null);
    setChartData([]);
    setDeviceData([]);
    setChannelData([]);
    setGeographyData([]);
    setTechnologyData([]);
    setTopPages([]);
    setRealtimeData(null);
    setEcommerceFunnel([]);
  };

  const loadAccountData = async (accountId: string) => {
    try {
      setLoadingData(true);
      setErroResumo(false);
      setErroDados(false);

      const { startDate, endDate } = getDefaultDateRange();

      // 🎯 USAR DADOS 100% REAIS DO BANCO - endpoint /complete-data
      const { data: complete } =
        await getCompleteDataApiV1IntegrationsGoogleAnalyticsCompleteDataGet({
          query: {
            account_id: accountId,
            start_date: startDate,
            end_date: endDate,
          },
          throwOnError: true,
        });
      setCompleteData(complete);

      // Carregar dados consolidados para overview
      let overview: GoogleAnalyticsOverviewData | null = null;
      try {
        const { data } =
          await getGoogleAnalyticsOverviewApiV1IntegrationsGoogleAnalyticsOverviewGet(
            {
              query: {
                account_id: accountId,
                start_date: startDate,
                end_date: endDate,
              },
              throwOnError: true,
            },
          );
        overview = data;
        setOverviewData(overview);
      } catch (overviewError) {
        setOverviewData(null);
        setErroResumo(true);

        toast({
          title: "Aviso",
          description:
            "Dados consolidados não disponíveis. Apenas dados básicos serão exibidos.",
          variant: "default",
        });
      }

      // Verificar se há dados reais disponíveis
      const hasRealData =
        complete &&
        ((complete.daily_metrics && complete.daily_metrics.length > 0) ||
          (complete.demographics && complete.demographics.length > 0) ||
          (complete.content_metrics && complete.content_metrics.length > 0) ||
          overview);

      if (!hasRealData) {
        toast({
          title: "Sem Dados Reais",
          description:
            "Nenhum dado foi encontrado no banco de dados. Execute uma sincronização para obter dados do Google Analytics.",
          variant: "destructive",
        });
        clearAllData();
        return;
      }

      // Processar dados das métricas diárias do banco para gráficos
      if (complete?.daily_metrics && complete.daily_metrics.length > 0) {
        const processedChartData = complete.daily_metrics.map(
          (metric: GoogleAnalyticsDailyMetric) => ({
            name: new Date(metric.date).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
            }),
            usuarios: metric.users || 0,
            sessoes: metric.sessions || 0,
            pageviews: metric.pageviews || 0,
            revenue: metric.revenue || 0,
            conversoes: metric.transactions || 0,
          }),
        );
        setChartData(processedChartData);
      } else {
        setChartData([]);
      }

      // Processar dados de dispositivos - REMOVIDO (não disponível na API)
      setDeviceData([]);

      // Processar dados de canais de aquisição - REMOVIDO (não disponível na API)
      setChannelData([]);

      // Processar funil de e-commerce com dados reais
      if (overview) {
        const totalUsers = overview.total_users || 1;
        const conversionRate = (overview.conversion_rate || 0) / 100;
        const actualPurchasers = Math.round(totalUsers * conversionRate);

        const ecommerceFunnelProcessed = [
          { stage: "Visitantes", users: totalUsers, color: "#e5e7eb" },
          {
            stage: "Visualizaram Produtos",
            users: Math.round(totalUsers * 0.6),
            color: "#93c5fd",
          },
          {
            stage: "Adicionaram ao Carrinho",
            users: Math.round(actualPurchasers * 3),
            color: "#60a5fa",
          },
          {
            stage: "Iniciaram Checkout",
            users: Math.round(actualPurchasers * 1.5),
            color: "#3b82f6",
          },
          { stage: "Compraram", users: actualPurchasers, color: "#1d4ed8" },
        ];
        setEcommerceFunnel(ecommerceFunnelProcessed);
      } else {
        setEcommerceFunnel([]);
      }

      // Usar dados geográficos dos dados completos do banco
      if (complete?.geography_data && complete.geography_data.length > 0) {
        setGeographyData(complete.geography_data);
      } else {
        setGeographyData([]);
      }

      // Usar dados de tecnologia dos dados completos do banco
      if (complete?.technology_data && complete.technology_data.length > 0) {
        setTechnologyData(complete.technology_data);
      } else {
        setTechnologyData([]);
      }

      // Usar dados de páginas dos dados completos do banco
      if (complete?.page_metrics && complete.page_metrics.length > 0) {
        // Processar e agrupar páginas por path
        const pagesGrouped = complete.page_metrics.reduce<
          Record<string, TopPageAccumulator>
        >(
          (
            acc: Record<string, TopPageAccumulator>,
            page: GoogleAnalyticsPageMetric,
          ) => {
            const path = page.page_path || "Desconhecido";
            if (!acc[path]) {
              acc[path] = {
                page_title: page.page_title || path,
                page_path: path,
                pageviews: 0,
                avg_time_on_page: 0,
                bounce_rate: 0,
                exit_rate: 0,
                count: 0,
              };
            }

            acc[path].pageviews += page.pageviews || 0;
            acc[path].avg_time_on_page += page.avg_time_on_page || 0;
            acc[path].bounce_rate += page.bounce_rate || 0;
            acc[path].exit_rate += page.exit_rate || 0;
            acc[path].count += 1;

            return acc;
          },
          {},
        );

        // Calcular médias e ordenar por pageviews
        const topPagesProcessed = Object.keys(pagesGrouped)
          .map((path) => {
            const page = pagesGrouped[path];
            return {
              page_title: page.page_title,
              page_path: page.page_path,
              pageviews: page.pageviews,
              avg_time_on_page: page.avg_time_on_page / page.count,
              bounce_rate: page.bounce_rate / page.count,
              exit_rate: page.exit_rate / page.count,
            };
          })
          .sort((a, b) => b.pageviews - a.pageviews)
          .slice(0, 10);

        setTopPages(topPagesProcessed);
      } else {
        setTopPages([]);
      }

      // Usar dados em tempo real dos dados completos do banco
      if (complete?.realtime_data) {
        setRealtimeData(complete.realtime_data);
      } else {
        setRealtimeData(null);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          "Erro ao carregar dados reais do Google Analytics. Verifique a conexão e sincronização.",
        variant: "destructive",
      });
      setErroResumo(true);
      setErroDados(true);
      clearAllData();
    } finally {
      setLoadingData(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const customerUuid = localStorage.getItem("active_customer_uuid");
      if (!customerUuid) {
        throw new Error(
          "active_customer_uuid ausente para desconectar Google Analytics",
        );
      }

      await disconnectGoogleAnalyticsApiV1IntegrationsGoogleAnalyticsDisconnectDelete(
        {
          query: { customer_uuid: customerUuid },
          throwOnError: true,
        },
      );

      toast({
        title: "Desconectado com Sucesso",
        description: "Conta do Google Analytics foi desconectada!",
        variant: "default",
      });

      // Limpar todos os dados e estados
      clearAllData();
      setStatus(null);
      setAccounts([]);
      setSelectedAccount("");

      // Forçar recarregamento do status
      try {
        const { data: newStatus } =
          await getGoogleAnalyticsStatusApiV1IntegrationsGoogleAnalyticsStatusGet(
            { throwOnError: true },
          );
        setStatus(newStatus);
      } catch (statusError) {
        setStatus({
          is_connected: false,
          error_message: "Integração desconectada",
        });
      }
    } catch (error) {
      toast({
        title: "Erro na Desconexão",
        description: "Erro ao desconectar conta do Google Analytics",
        variant: "destructive",
      });
    }
  };

  const handleConnect = async () => {
    try {
      // Obter URL de autenticação através do microsserviço Nexus
      const { data: authData } =
        await getGoogleAnalyticsAuthUrlApiV1IntegrationsNexusGoogleAnalyticsAuthUrlGet(
          { throwOnError: true },
        );

      // Redirecionar para a URL de autenticação do Google
      window.location.href = authData.url;
    } catch (error) {
      toast({
        title: "Erro na Conexão",
        description: "Erro ao iniciar processo de conexão com Google Analytics",
        variant: "destructive",
      });
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const formatCurrency = (value: number): string => {
    if (!value || isNaN(value)) return "R$ 0,00";
    return formatCurrencyUtil(value);
  };

  const formatPercentage = (value: number): string => {
    if (!value || isNaN(value)) return "0,00%";
    return formatPercentUtil(value);
  };

  const formatNumber = (value: number): string => {
    if (!value || isNaN(value)) return "0";
    return formatIntegerUtil(value);
  };

  const TrendIcon = ({
    value,
    className = "",
  }: {
    value: number;
    className?: string;
  }) => {
    if (value > 0)
      return <ArrowUp className={`h-3 w-3 text-success-soft ${className}`} />;
    if (value < 0)
      return <ArrowDown className={`h-3 w-3 text-error-soft ${className}`} />;
    return <Minus className={`h-3 w-3 text-gray-500 ${className}`} />;
  };

  // Se não há dados e não está carregando, mostrar estado vazio
  if (
    !loading &&
    !loadingData &&
    (!status || !status.is_connected) &&
    accounts.length === 0
  ) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="lg:block hidden">
            <h1 className="text-3xl font-bold tracking-tight">
              Google Analytics
            </h1>
            <p className="text-muted-foreground">
              Análise detalhada do comportamento dos usuários no seu site
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="bg-gray-50 text-gray-700">
              Não Conectado
            </Badge>
            <Button variant="outline" onClick={handleConnect}>
              <Settings className="h-4 w-4 mr-2" />
              Conectar
            </Button>
          </div>
        </div>

        <div className="text-center py-12">
          <Globe className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Google Analytics não conectado
          </h3>
          <p className="text-muted-foreground mb-4">
            Conecte sua conta do Google Analytics para ver dados reais dos seus
            usuários.
          </p>
          <Button onClick={handleConnect}>
            <Settings className="h-4 w-4 mr-2" />
            Conectar Google Analytics
          </Button>
        </div>
      </div>
    );
  }

  // Calcular estatísticas principais com verificações de segurança
  type StatItem = {
    title: string;
    rawValue: number | null;
    format: (n: number) => string;
    change: string;
    trend: "up" | "down";
    icon: ComponentType<{ className?: string }>;
  };

  /** Número do GA4 que não veio: indicador enquanto a busca está em voo, N/D com o motivo depois. */
  const semDado = (erro: boolean) =>
    loading || loadingData ? (
      <CarregandoValor />
    ) : (
      <ValorIndisponivel fontes={FONTES_GA4} erro={erro} />
    );

  const stats: StatItem[] = [
    {
      title: "Usuários Únicos",
      rawValue: numeroOuNulo(overviewData?.total_users),
      format: (n: number) => formatNumber(n),
      change: "",
      trend: "up" as const,
      icon: Users,
    },
    {
      title: "Sessões",
      rawValue: numeroOuNulo(overviewData?.total_sessions),
      format: (n: number) => formatNumber(n),
      change: "",
      trend: "up" as const,
      icon: Eye,
    },
    {
      title: "Receita Total",
      rawValue: numeroOuNulo(overviewData?.total_revenue),
      format: (n: number) => formatCurrency(n),
      change: "",
      trend: "up" as const,
      icon: DollarSign,
    },
    {
      title: "Taxa de Conversão",
      rawValue: numeroOuNulo(overviewData?.conversion_rate),
      format: (n: number) => formatPercentage(n),
      change: "",
      trend: "up" as const,
      icon: Target,
    },
  ];

  return (
    <IntegrationErrorBoundary
      integrationContext={{
        type: "google_analytics",
        name: "Google Analytics",
        id: "google-analytics-main",
      }}
    >
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="lg:block hidden">
            <h1 className="text-3xl font-bold tracking-tight">
              Google Analytics
            </h1>
            <p className="text-muted-foreground">
              Análise detalhada do comportamento dos usuários no seu site
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* Seletor de Propriedades */}
            {accounts.length > 0 && (
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  loadAccountData(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            )}

            <Badge
              variant="outline"
              className={
                loadingData || loading
                  ? "bg-blue-50 text-blue-700"
                  : status?.is_connected
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-50 text-gray-700"
              }
            >
              {loadingData || loading
                ? "🔄 Carregando dados..."
                : status?.is_connected
                  ? "Conectado"
                  : "Carregando..."}
            </Badge>
            {status?.last_sync && (
              <div className="text-xs text-muted-foreground">
                Última sync:{" "}
                {new Date(status.last_sync).toLocaleString("pt-BR")}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDisconnect}
                disabled={loading || loadingData}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Desconectar
              </Button>
            </div>
          </div>
        </div>

        {/* Filtro Global */}
        <GlobalFilter className="mb-6" />

        {/* Stats Cards — os títulos são fixos; durante a busca, o indicador fica no lugar de cada número */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card
              key={stat.title}
              className="hover:shadow-md transition-shadow"
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="flex items-center gap-1 min-w-0">
                  <span className="truncate">{stat.title}</span>
                  <HelpTooltip
                    content={getKpiTooltip(stat.title)}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={loading || loadingData}
                      disponivel={
                        stat.rawValue !== null && Number.isFinite(stat.rawValue)
                      }
                      fontes={FONTES_GA4}
                      erro={erroResumo}
                      className="text-2xl font-bold text-muted-foreground"
                    >
                      <KpiNumber
                        value={stat.rawValue ?? 0}
                        format={stat.format}
                        className="font-bold text-foreground"
                        maxFontPx={30}
                        breathingRoomPx={18}
                        ariaLabel={stat.title}
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled && stat.change && (
                    <div className="flex items-center gap-1 text-xs">
                      {stat.trend === "up" ? (
                        <TrendingUp className="h-3 w-3 text-success-soft" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-error-soft" />
                      )}
                      <span
                        className={
                          stat.trend === "up"
                            ? "text-success-soft"
                            : "text-error-soft"
                        }
                      >
                        {stat.change}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs para organizar o conteúdo */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="acquisition" className="text-xs sm:text-sm">
              Aquisição
            </TabsTrigger>
            <TabsTrigger value="behavior" className="text-xs sm:text-sm">
              Comportamento
            </TabsTrigger>
            <TabsTrigger value="ecommerce" className="text-xs sm:text-sm">
              E-commerce
            </TabsTrigger>
            <TabsTrigger value="audience" className="text-xs sm:text-sm">
              Audiência
            </TabsTrigger>
            <TabsTrigger value="realtime" className="text-xs sm:text-sm">
              Tempo Real
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Charts */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              <Card className="col-span-5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChartIcon className="h-5 w-5" />
                    Evolução da Audiência
                  </CardTitle>
                  <CardDescription>
                    Usuários, sessões e pageviews ao longo do tempo (dados reais
                    do banco)
                  </CardDescription>
                </CardHeader>
                <CardContent className="pl-2">
                  {loading || loadingData ? (
                    <ChartSkeleton height={350} />
                  ) : chartData.length > 0 ? (
                    <ScrollableChart>
                      <TremorLineChart
                        className="h-80"
                        data={chartData}
                        index="name"
                        categories={["usuarios", "sessoes", "pageviews"]}
                        colors={["blue", "emerald", "lime"]}
                        valueFormatter={(value) => formatIntegerUtil(value)}
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    </ScrollableChart>
                  ) : (
                    <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                      Nenhum dado disponível para o período selecionado
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Dispositivos
                  </CardTitle>
                  <CardDescription>
                    Distribuição por tipo de dispositivo (dados reais)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading || loadingData ? (
                    <ChartSkeleton height={350} />
                  ) : deviceData.length > 0 &&
                    deviceData.some((d) => d.value > 0) ? (
                    <TremorDonutChart
                      className="h-80"
                      data={deviceData}
                      index="name"
                      category="value"
                      colors={["blue", "emerald", "amber"]}
                      valueFormatter={(value) => formatPercentage(value)}
                      showLabel={true}
                      showAnimation={true}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                      Nenhum dado disponível
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Revenue Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Tendência de Receita
                </CardTitle>
                <CardDescription>
                  Evolução da receita e conversões ao longo do tempo (dados
                  reais do banco)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading || loadingData ? (
                  <ChartSkeleton height={300} />
                ) : chartData.length > 0 ? (
                  <ScrollableChart>
                    <TremorBarChart
                      className="h-72"
                      data={chartData}
                      index="name"
                      categories={["revenue", "conversoes"]}
                      colors={["emerald", "amber"]}
                      valueFormatter={(value) => formatIntegerUtil(value)}
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    Nenhum dado disponível
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Pages Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Páginas Mais Visitadas
                </CardTitle>
                <CardDescription>
                  Performance das principais páginas do seu site (dados reais do
                  banco)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading || loadingData ? (
                  <div className="space-y-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Skeleton className="h-6 w-6" />
                          <div>
                            <Skeleton className="h-4 w-48 mb-2" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                        </div>
                        <div className="flex gap-6">
                          <div className="text-center">
                            <Skeleton className="h-4 w-12 mb-1" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                          <div className="text-center">
                            <Skeleton className="h-4 w-12 mb-1" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : topPages.length > 0 ? (
                  <div className="space-y-4">
                    {topPages.map((page, index) => (
                      <div
                        key={page.page_path}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-sm bg-white px-2 py-1 rounded">
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium">{page.page_title}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(page.pageviews)} visualizações
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <div className="text-center">
                            <p className="font-medium">
                              {formatTime(page.avg_time_on_page)}
                            </p>
                            <p className="text-muted-foreground">Tempo médio</p>
                          </div>
                          <div className="text-center">
                            <p className="font-medium">
                              {formatPercentage(page.bounce_rate)}
                            </p>
                            <p className="text-muted-foreground">
                              Taxa de rejeição
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhuma página encontrada para o período selecionado
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="acquisition" className="space-y-4">
            {/* Canais de Aquisição */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="h-5 w-5" />
                    Canais de Aquisição
                  </CardTitle>
                  <CardDescription>
                    Origem do tráfego por canal (dados reais do banco)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading || loadingData ? (
                    <ChartSkeleton height={300} />
                  ) : channelData.length > 0 ? (
                    <ScrollableChart>
                      <TremorBarChart
                        className="h-72"
                        data={channelData}
                        index="name"
                        categories={["sessions"]}
                        colors={["blue"]}
                        valueFormatter={(value) => formatIntegerUtil(value)}
                        showLegend={false}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    </ScrollableChart>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      Nenhum dado disponível
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Detalhes dos Canais</CardTitle>
                  <CardDescription>
                    Performance detalhada por canal (dados reais)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {channelData.map((channel, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: channel.color }}
                          ></div>
                          <div>
                            <p className="font-medium">{channel.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(channel.sessions)} sessões
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            {formatCurrency(channel.revenue)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatPercentage(channel.percentage)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="behavior" className="space-y-4">
            {/* Métricas de Comportamento */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Taxa de Engajamento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">
                    {overviewData
                      ? formatPercentage(overviewData.engagement_rate || 0)
                      : semDado(erroResumo)}
                  </div>
                  <Progress
                    value={overviewData?.engagement_rate || 0}
                    className="h-2"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    {overviewData
                      ? formatNumber(overviewData.engaged_sessions || 0)
                      : semDado(erroResumo)}{" "}
                    sessões engajadas
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Tempo Médio na Página
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">
                    {overviewData
                      ? formatTime(overviewData.avg_session_duration || 0)
                      : semDado(erroResumo)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Tempo médio de sessão
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Páginas por Sessão
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">
                    {overviewData
                      ? (
                          (overviewData.total_pageviews || 0) /
                          Math.max(overviewData.total_sessions || 1, 1)
                        ).toFixed(1)
                      : semDado(erroResumo)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Pageviews por sessão
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ecommerce" className="space-y-4">
            {/* E-commerce Metrics */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-1 min-w-0">
                    <span className="truncate">Receita Total</span>
                    <HelpTooltip
                      content={getKpiTooltip("Receita Total")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {overviewData
                      ? formatCurrency(overviewData.total_revenue || 0)
                      : semDado(erroResumo)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-1 min-w-0">
                    <span className="truncate">Transações</span>
                    <HelpTooltip
                      content={getKpiTooltip("Transações")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {overviewData
                      ? formatNumber(overviewData.total_transactions || 0)
                      : semDado(erroResumo)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-1 min-w-0">
                    <span className="truncate">Valor Médio do Pedido</span>
                    <HelpTooltip
                      content={getKpiTooltip("Valor Médio do Pedido")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {overviewData
                      ? formatCurrency(overviewData.average_order_value || 0)
                      : semDado(erroResumo)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-1 min-w-0">
                    <span className="truncate">
                      Taxa de Conversão E-commerce
                    </span>
                    <HelpTooltip
                      content={getKpiTooltip("Taxa de Conversão E-commerce")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {overviewData
                      ? formatPercentage(overviewData.conversion_rate || 0)
                      : semDado(erroResumo)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Funil de E-commerce */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Funil de Conversão
                </CardTitle>
                <CardDescription>
                  Jornada do usuário desde a visita até a compra (baseado em
                  dados reais)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ecommerceFunnel.map((stage, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-32 text-sm font-medium">
                        {stage.stage}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={
                              index === 0
                                ? 100
                                : Math.max(
                                    (stage.users /
                                      Math.max(ecommerceFunnel[0].users, 1)) *
                                      100,
                                    0,
                                  )
                            }
                            className="h-6 flex-1"
                          />
                          <div className="w-24 text-right">
                            <span className="font-medium">
                              {formatNumber(stage.users)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {index > 0 && (
                        <div className="w-16 text-right text-sm text-muted-foreground">
                          {formatPercentage(
                            (stage.users /
                              Math.max(ecommerceFunnel[index - 1].users, 1)) *
                              100,
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audience" className="space-y-4">
            {/* Demografia */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Principais Países
                  </CardTitle>
                  <CardDescription>
                    Distribuição geográfica dos usuários (dados reais do banco)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topCountries.length > 0 ? (
                      topCountries.map((country) => (
                        <div
                          key={country.country}
                          className="flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium">{country.country}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(country.sessions)} sessões
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              {formatCurrency(country.revenue)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {country.conversion_rate === null
                                ? semDado(false)
                                : formatPercentage(
                                    country.conversion_rate,
                                  )}{" "}
                              conv.
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Nenhum dado geográfico disponível
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Chrome className="h-5 w-5" />
                    Navegadores
                  </CardTitle>
                  <CardDescription>
                    Distribuição por navegador (dados reais do banco)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topBrowsers.length > 0 ? (
                      topBrowsers.map((browser) => (
                        <div
                          key={browser.browser}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium">{browser.browser}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatNumber(browser.sessions)} sessões
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              {formatCurrency(browser.revenue)}
                            </p>
                            <Progress
                              value={proporcaoDoLider(browser, topBrowsers[0])}
                              className="h-2 w-24"
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Nenhum dado de tecnologia disponível
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="realtime" className="space-y-4">
            {/* Real-time data */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Usuários Online
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {realtimeData
                      ? formatNumber(realtimeData.active_users || 0)
                      : semDado(erroDados)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Agora mesmo (dados reais)
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Taxa de Rejeição</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {overviewData
                      ? formatPercentage(overviewData.bounce_rate || 0)
                      : semDado(erroResumo)}
                  </div>
                  <div className="flex items-center space-x-1 text-sm">
                    <span className="text-muted-foreground">
                      Últimos 30 dias
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Duração Média da Sessão</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {overviewData
                      ? formatTime(overviewData.avg_session_duration || 0)
                      : semDado(erroResumo)}
                  </div>
                  <div className="flex items-center space-x-1 text-sm">
                    <span className="text-muted-foreground">
                      Últimos 30 dias
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Página mais popular em tempo real */}
            {realtimeData?.top_page_title && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Página Mais Popular (Tempo Real)
                  </CardTitle>
                  <CardDescription>
                    Dados em tempo real do banco de dados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {realtimeData.top_page_title}
                      </p>
                      {/* M-183: a API de tempo real do GA4 não expõe `pagePath`, então
                          `top_page_path` é sempre nulo. A linha só aparece se algum dia
                          houver caminho — hoje ela fica de fora em vez de desenhar um
                          parágrafo vazio sob o título. */}
                      {realtimeData.top_page_path && (
                        <p className="text-sm text-muted-foreground">
                          {realtimeData.top_page_path}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">
                        {formatNumber(realtimeData.top_page_views || 0)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        visualizações ativas
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {(!realtimeData || !realtimeData.top_page_title) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Página Mais Popular (Tempo Real)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum dado em tempo real disponível
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </IntegrationErrorBoundary>
  );
};

export default GoogleAnalyticsPage;
